<template>
  <section class="pitch-owner-registration">
    <MainNavbar class="navbar-overlay" />
    <ToastContainer :toasts="toasts" @remove="removeToast" />

    <div class="hero-section">
      <div class="hero-content">
        <h1>Become a Sport Facility Owner</h1>
        <p>List your sports facility and start earning from bookings today</p>
      </div>
    </div>

    <div class="registration-container">
      <div class="form-card fade-up">
        <div class="form-header">
          <h2>Sport Facility Owner Registration</h2>
          <p>Fill in your sport facility details to get started</p>
        </div>

        <div class="contact-visibility-notice" role="status">
          <i class="fa-solid fa-address-card" aria-hidden="true"></i>
          <div class="contact-visibility-notice__body">
            <strong>How customers reach you</strong>
            <p>
              Your <strong>email and phone number</strong> from your IpromiseAfrica profile will be shown to
              other customers on your facility listing so they can contact you about bookings and enquiries. By
              registering, you agree to this visibility for operational communication on the platform.
            </p>
          </div>
        </div>

        <form @submit.prevent="submitRegistration" class="registration-form">
          <div class="form-section">
            <h3>Sport Facility Information</h3>
            <div class="form-grid">
              <div class="input-group">
                <label for="pitchTier">Sport Facility Tier *</label>
                <select id="pitchTier" v-model="formData.pitch_tier" required :disabled="isLoading">
                  <option value="">Select sport facility tier</option>
                  <option value="premium">Premium</option>
                  <option value="standard">Standard</option>
                  <option value="basic">Basic</option>
                </select>
              </div>

              <div class="input-group">
                <label for="pitchName">Sport Facility Name *</label>
                <input id="pitchName" v-model="formData.pitch_name" type="text" required placeholder="e.g., Greenfield Arena" :disabled="isLoading" />
              </div>

              <div class="input-group">
                <label for="pitchType">Sport facility type *</label>
                <select id="pitchType" v-model="formData.pitch_type" required :disabled="isLoading">
                  <option value="">Select sport facility type</option>
                  <option
                    v-for="opt in facilityTypeOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>

              <div class="input-group">
                <label for="hourlyRate">Hourly Rate (Ksh) *</label>
                <input id="hourlyRate" v-model="formData.hourly_rate" type="number" required min="0" placeholder="2000" :disabled="isLoading" />
              </div>

              <div class="input-group">
                <label for="capacity">Maximum players at the sport facility</label>
                <input id="capacity" v-model="formData.capacity" type="number" min="0" placeholder="e.g., 50" :disabled="isLoading" />
              </div>
              <div class="input-group full-width">
                <label for="pitchDescription">Sport Facility Description *</label>
                <textarea 
                  id="pitchDescription" 
                  v-model="formData.pitch_description" 
                  required 
                  placeholder="Describe your sport facility features, amenities, and any other important details..." 
                  :disabled="isLoading"
                  rows="4"
                  maxlength="500"
                ></textarea>
                <small>
                  <i class="fa-solid fa-info-circle"></i>
                  Describe your sport facility in detail (max 500 characters). Include surface type, lighting, spectator areas, etc.
                </small>
              </div>
            </div>
          </div>

          <div class="form-section">
            <h3>Location Details</h3>
            <div class="form-grid">
              <div class="input-group full-width">
                <label for="address">Full Address *</label>
                <input id="address" v-model="formData.address" type="text" required placeholder="Street address, building, etc." :disabled="isLoading" />
              </div>

              <div class="input-group">
                <label for="county">County *</label>
                <select id="county" v-model="formData.county" required :disabled="isLoading" @change="updateZoneOptions">
                  <option value="">Select county</option>
                  <option value="Nairobi">Nairobi</option>
                  <option value="Mombasa">Mombasa</option>
                  <option value="Kisumu">Kisumu</option>
                  <option value="Kiambu">Kiambu</option>
                </select>
              </div>

              <div class="input-group">
                <label for="zone">Zone *</label>
                <select id="zone" v-model="formData.zone" required :disabled="isLoading || !formData.county">
                  <option value="">Select zone</option>
                  <option v-for="zone in zoneOptions" :key="zone" :value="zone">{{ zone }}</option>
                </select>
              </div>

              <div class="input-group">
                <label for="country">Country</label>
                <input
                  id="country"
                  v-model="formData.country"
                  type="text"
                  readonly
                  class="input-readonly"
                  tabindex="-1"
                  aria-readonly="true"
                />
                <small class="country-hint">Registrations are only available in Kenya.</small>
              </div>

              <div class="input-group full-width">
                <label for="pinLocationUrl">Pin Location URL *</label>
                <input 
                  id="pinLocationUrl" 
                  v-model="formData.pin_location_url" 
                  type="url" 
                  required 
                  placeholder="https://maps.google.com/?q=latitude,longitude" 
                  :disabled="isLoading" 
                />
                <small>
                  <i class="fa-solid fa-info-circle"></i>
                  Get your location URL from Google Maps: Right-click on your sport facility location → "What's here?" → Copy the coordinates URL
                </small>
              </div>
            </div>

            <div class="input-group">
              <label for="facilityImages">Sport facility photos</label>
              <input
                id="facilityImages"
                type="file"
                multiple
                accept=".jpg,.jpeg,.png"
                @change="handleFacilityImagesUpload"
                :disabled="isLoading"
              />
              <small>Upload up to {{ maxFacilityImages }} photos (JPG or PNG, max 10MB each)</small>
              <p v-if="imageUploadError" class="image-upload-error">
                <i class="fa-solid fa-circle-exclamation"></i> {{ imageUploadError }}
              </p>
            </div>
          </div>

          <div class="form-section">
            <h3>Facility Amenities</h3>
            <div class="amenities-grid">
              <label v-for="amenity in amenities" :key="amenity.id" class="amenity-checkbox">
                <input type="checkbox" :value="amenity.id" v-model="formData.amenities" :disabled="isLoading" />
                <span class="checkmark"></span>
                <i :class="amenity.icon"></i>
                {{ amenity.name }}
              </label>
            </div>
          </div>

          <div class="form-section">
            <label class="terms-checkbox">
              <input type="checkbox" v-model="formData.agree_to_terms" required :disabled="isLoading" />
              <span class="checkmark"></span>
              <span class="terms-checkbox-text">
                I agree to the
                <router-link :to="{ name: 'FacilityOwnershipTerms' }" @click.stop>
                  Facility Ownership Terms and Conditions
                </router-link>.
              </span>
            </label>

            <div class="form-actions">
              <button type="submit" class="submit-btn" :class="{ loading: isLoading }" :disabled="isLoading">
                <span class="submit-btn-inner">
                  <i v-if="!isLoading" class="fa-solid fa-user-plus" aria-hidden="true"></i>
                  <i v-else class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                  {{ isLoading ? "Processing" : "Register as Sport Facility Owner" }}
                </span>
              </button>

              <button type="button" class="cancel-btn" @click="$router.back()" :disabled="isLoading">
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div class="benefits-section">
      <h2>Why List Your Sport Facility With Us?</h2>
      <div class="benefits-grid">
        <div
          class="benefit-card"
          v-for="(benefit, i) in benefits"
          :key="i"
          :class="{ 'benefit-card--coming-soon': benefit.comingSoon }"
        >
          <i :class="benefit.icon"></i>
          <h3>{{ benefit.title }}</h3>
          <p>{{ benefit.description }}</p>
          <span
            v-if="benefit.comingSoon"
            class="coming-soon-badge"
            :title="benefit.hoverHint"
          >Coming soon</span>
        </div>
      </div>
    </div>
    <MainFooter />
  </section>
</template>

<script>
/**
 * @fileoverview Pitch owner self-registration for logged-in IPA customers.
 * @module routes/facility/RegisterFacility
 *
 * Entry from the facility landing (`BookFacility.vue` → list your facility) or direct route.
 *
 * ## Purpose
 * Collects facility (pitch) details and submits them as `multipart/form-data` to register
 * the current user as a pitch owner. Name, email, and phone are **not** collected on this form;
 * the API uses the authenticated customer profile. The UI explains that those contact details
 * are shown to other IPA users for bookings and communication.
 *
 * ## Backend
 * - **Endpoint:** `POST /api/pitch-owners/create` via `buildApiUrl` (`@/config/api`).
 * - **Auth:** `Authorization: Bearer <access_token>`.
 * - **Body:** Fields in `PITCH_CREATE_FORM_KEYS`, `amenities` (comma-separated ids), optional
 *   `pitch_images` files. Only whitelisted keys are sent (`agree_to_terms` stays client-only).
 *
 * ## Error handling (HTTP)
 * - **401 / 403:** Clear session, toast, redirect sign-in.
 * - **400:** Special case — “already have a pitch ownership account” → modal; else parsed message.
 * - **429:** Rate limit — friendly retry message.
 * - **5xx / network:** Generic “Unable to register” / connection copy — no raw stack traces in toasts.
 * - **422 / other 4xx:** Validation or `detail` parsed into short user-facing messages.
 *
 * ## UX
 * - Toasts: global `show-toast` event (`title`, `message`, `type`).
 * - Success: `router.push` to pitch dashboard, then success toast.
 * - Animations: `IntersectionObserver` on `.fade-up` (guarded in non-browser environments).
 *
 * ## Validation (client)
 * Required fields, hourly rate &gt; 0, pin URL, description ≥ 20 chars, terms, optional images
 * JPG/PNG ≤ 10MB.
 *
 * ## Related routes
 * Sign-in: `/ipromiseafrica/sign-in`. Facility terms: `/ipromiseafrica/legal/facility-ownership-terms`. Dashboard: `/ipromiseafrica/facility/my-facility`.
 */

import MainNavbar from "../../components/main/headernfooter/header.vue";
import MainFooter from "../../components/main/headernfooter/Footer.vue";
import ToastContainer from "@/components/ui/ToastContainer.vue";
import toastMixin from "@/mixins/toastMixin";
import { buildApiUrl } from "@/config/api";
import { FACILITY_TYPE_OPTIONS } from "@/utils/facilityTypes";
import {
  AUTH_REDIRECT_PATHS,
  navigateToSignIn,
} from "@/utils/authRedirect";

const MAX_FACILITY_IMAGES = 4;

/** Form field names sent to `POST /api/pitch-owners/create` (must stay in sync with API). */
const PITCH_CREATE_FORM_KEYS = [
  "pitch_tier",
  "pitch_name",
  "pitch_type",
  "hourly_rate",
  "capacity",
  "pitch_description",
  "address",
  "county",
  "country",
  "zone",
  "pin_location_url",
];

export default {
  name: "FacilityOwnerRegistration",
  mixins: [toastMixin],
  components: { MainNavbar, MainFooter, ToastContainer },
  data() {
    return {
      maxFacilityImages: MAX_FACILITY_IMAGES,
      facilityTypeOptions: FACILITY_TYPE_OPTIONS,
      isLoading: false,
      imageUploadError: "",
      zoneOptions: [],
      formData: {
        pitch_name: "",
        pitch_type: "",
        hourly_rate: "",
        capacity: "",
        pitch_tier: "",
        pitch_description: "",
        address: "",
        county: "",
        zone: "",
        country: "Kenya",
        pin_location_url: "",
        pitch_images: [],
        amenities: [],
        agree_to_terms: false,
      },
      zonesByCounty: {
        Nairobi: [
          "Westlands",
          "Dagoretti North",
          "Dagoretti South",
          "Langata",
          "Kibra",
          "Roysambu",
          "Kasarani",
          "Ruaraka",
          "Embakasi South",
          "Embakasi North",
          "Embakasi Central",
          "Embakasi East",
          "Embakasi West",
          "Makadara",
          "Kamukunji",
          "Starehe"
        ],
        Mombasa: [
          "Changamwe",
          "Jomvu",
          "Kisauni",
          "Likoni",
          "Mvita",
          "Nyali"
        ],
        Kisumu: [
          "Kisumu Central",
          "Kisumu East",
          "Kisumu West",
          "Seme",
          "Nyando",
          "Muhoroni",
          "Nyakach"
        ],
        Kiambu: [
          "Gatundu North",
          "Gatundu South",
          "Githunguri",
          "Juja",
          "Kabete",
          "Kiambaa",
          "Kiambu Town",
          "Kikuyu",
          "Lari",
          "Limuru",
          "Ruiru",
          "Thika Town"
        ]
      },
      amenities: [
        { id: "lighting", name: "Flood Lighting", icon: "fa-solid fa-lightbulb" },
        { id: "changing", name: "Changing Rooms", icon: "fa-solid fa-person-booth" },
        { id: "parking", name: "Parking Space", icon: "fa-solid fa-car" },
        { id: "showers", name: "Showers", icon: "fa-solid fa-shower" },
        { id: "equipment", name: "Equipment Rental", icon: "fa-solid fa-basketball" },
        { id: "cafe", name: "Cafe/Snacks", icon: "fa-solid fa-utensils" },
        { id: "wifi", name: "Free WiFi", icon: "fa-solid fa-wifi" },
        { id: "first_aid", name: "First Aid Kit", icon: "fa-solid fa-kit-medical" }
      ],
      benefits: [
        {
          icon: "fa-solid fa-chart-line",
          title: "Increase Your Revenue",
          description: "Maximize your sport facility utilization with our booking platform and reach more customers."
        },
        {
          icon: "fa-solid fa-calendar-check",
          title: "Easy Management",
          description: "Manage bookings, availability, and payments all in one place with our intuitive dashboard."
        },
        {
          icon: "fa-solid fa-shield-alt",
          title: "Secure Payments",
          description: "Get paid securely and on time with our reliable payment processing system.",
          comingSoon: true,
          hoverHint: "Integrating with popular mobile and internet banking services"
        },
        {
          icon: "fa-solid fa-users",
          title: "Grow Your Clientele",
          description: "Reach thousands of sports enthusiasts looking for quality sport facilities in your area."
        }
      ]
    };
  },
  mounted() {
    this.observeAnimations();
    this.checkAuthentication();
  },
  methods: {
    observeAnimations() {
      if (typeof document === "undefined") return;
      const nodes = document.querySelectorAll(".fade-up");
      if (!nodes.length) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );

      nodes.forEach((el) => observer.observe(el));
    },

    updateZoneOptions() {
      this.formData.zone = "";
      if (this.formData.county && this.zonesByCounty[this.formData.county]) {
        this.zoneOptions = this.zonesByCounty[this.formData.county];
      } else {
        this.zoneOptions = [];
      }
    },

    checkAuthentication() {
      const token = localStorage.getItem("access_token");
      if (!token) {
        this.emitToast(
          "Sign in required",
          "Please log in to register as a sport facility owner.",
          "warning"
        );
        setTimeout(() => {
          navigateToSignIn(this.$router, AUTH_REDIRECT_PATHS.FACILITY_OWNER_REGISTER);
        }, 1500);
      }
    },

    emitToast(title, message, type = "info") {
      this.showToast(title, message, type);
    },


    /** Generic copy for 5xx/infra failures — avoids exposing internal error text. */
    emitToastServerUnavailable() {
      this.emitToast(
        "Unable to register",
        "We couldn't complete your registration right now. Please try again in a few minutes.",
        "error"
      );
    },

    isServerErrorStatus(status) {
      return typeof status === "number" && status >= 500 && status <= 599;
    },

    /** Defensive: some thrown errors still echo "500" or "Internal Server Error". */
    isGenericServerFailureMessage(error) {
      const msg = String((error && error.message) || "").toLowerCase();
      return (
        msg.includes("internal server error") ||
        msg.includes("status code 500") ||
        /\b500\b/.test(msg)
      );
    },

    truncateUserMessage(text, maxLen = 220) {
      const s = String(text || "").trim();
      if (s.length <= maxLen) return s;
      return `${s.slice(0, maxLen - 1)}…`;
    },

    showAlreadyRegisteredMessage() {
      this.showToast(
        "warning",
        "Already registered",
        "You already have a facility ownership account. Open your dashboard to manage your listing."
      );
      setTimeout(() => {
        this.$router.push("/ipromiseafrica/facility/my-facility");
      }, 4000);
    },

    handleFacilityImagesUpload(event) {
      this.imageUploadError = "";
      const files = event.target.files;
      if (!files || files.length === 0) {
        this.formData.pitch_images = [];
        return;
      }
      const selected = Array.from(files);
      if (selected.length > MAX_FACILITY_IMAGES) {
        this.imageUploadError = `You can upload at most ${MAX_FACILITY_IMAGES} photos.`;
        this.formData.pitch_images = selected.slice(0, MAX_FACILITY_IMAGES);
        event.target.value = "";
        return;
      }
      this.formData.pitch_images = selected;
    },

    async submitRegistration() {
      if (!this.validateForm()) return;

      const token = localStorage.getItem("access_token");
      if (!token) {
        this.emitToast("Sign in required", "Please log in to continue.", "warning");
        setTimeout(() => {
          navigateToSignIn(this.$router, AUTH_REDIRECT_PATHS.FACILITY_OWNER_REGISTER);
        }, 1500);
        return;
      }

      this.isLoading = true;

      try {
        const formData = new FormData();
        PITCH_CREATE_FORM_KEYS.forEach((key) => {
          const v = this.formData[key];
          if (v !== null && v !== undefined && v !== "") {
            formData.append(key, v);
          }
        });
        if (!formData.has("country")) {
          formData.append("country", this.formData.country || "Kenya");
        }
        formData.append("amenities", (this.formData.amenities || []).join(","));
        (this.formData.pitch_images || []).forEach((file) => {
          formData.append("pitch_images", file);
        });

        const response = await fetch(buildApiUrl("/api/facilities/create"), {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            localStorage.removeItem("access_token");
            localStorage.removeItem("user");
            this.emitToast(
              "Session expired",
              "Please log in again.",
              "error"
            );
            setTimeout(() => {
              navigateToSignIn(this.$router, AUTH_REDIRECT_PATHS.FACILITY_OWNER_REGISTER);
            }, 1500);
            return;
          }

          const errorData = await response.json().catch(() => ({}));

          if (response.status === 400) {
            const alreadyMsg = this.extractDetailString(errorData.detail);
            if (
              typeof alreadyMsg === "string" &&
              (alreadyMsg.includes("already have a pitch ownership account") ||
                alreadyMsg.includes("already have a facility ownership account"))
            ) {
              this.showAlreadyRegisteredMessage();
              return;
            }
          }

          if (this.isServerErrorStatus(response.status)) {
            this.emitToastServerUnavailable();
            return;
          }

          if (response.status === 429) {
            this.emitToast(
              "Too many attempts",
              "Please wait a minute before trying again.",
              "warning"
            );
            return;
          }

          const errorMessage = this.parseErrorMessage(errorData, response.status);
          throw new Error(errorMessage);
        }

        await response.json().catch(() => ({}));
        const facilityName = (this.formData.pitch_name || "").trim() || "Your facility";

        try {
          await this.$router.push("/ipromiseafrica/facility/my-facility");
        } catch (navErr) {
          console.warn("RegisterFacility: navigation after register", navErr);
        }
        this.emitToast(
          "Sport facility registered",
          `${facilityName} is registered. You can manage it from your sport facility dashboard.`,
          "success"
        );
      } catch (error) {
        console.error("RegisterFacility: submitRegistration", error);
        if (this.isLikelyNetworkError(error)) {
          this.emitToast(
            "Connection problem",
            "Please check your network and try again.",
            "error"
          );
        } else if (this.isGenericServerFailureMessage(error)) {
          this.emitToastServerUnavailable();
        } else {
          this.emitToast(
            "Registration failed",
            this.truncateUserMessage((error && error.message) || "Please check your details and try again."),
            "error"
          );
        }
      } finally {
        this.isLoading = false;
      }
    },

    extractDetailString(detail) {
      if (detail == null) return "";
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail) && detail.length) {
        const first = detail[0];
        if (typeof first === "string") return first;
        if (first && typeof first.msg === "string") return first.msg;
      }
      return "";
    },

    isLikelyNetworkError(error) {
      if (!error) return false;
      if (error.name === "TypeError") return true;
      const msg = String(error.message || "").toLowerCase();
      return (
        msg.includes("failed to fetch") ||
        msg.includes("network") ||
        msg.includes("load failed") ||
        msg.includes("aborted")
      );
    },

    parseErrorMessage(errorData, status) {
      if (!errorData) {
        return status
          ? `Request failed (${status}). Please try again.`
          : "An unknown error occurred";
      }
      if (typeof errorData === "string") return this.truncateUserMessage(errorData);
      if (errorData.detail) {
        if (Array.isArray(errorData.detail)) {
          const parts = errorData.detail.map((err) => {
            if (err == null) return "";
            if (typeof err === "string") return err;
            const loc = Array.isArray(err.loc) ? err.loc.filter(Boolean).join(".") : "";
            const msg = err.msg != null ? String(err.msg) : JSON.stringify(err);
            return loc ? `${loc}: ${msg}` : msg;
          });
          const joined = parts.filter(Boolean).join("; ");
          return this.truncateUserMessage(
            joined || `Validation failed${status ? ` (${status})` : ""}.`
          );
        }
        if (typeof errorData.detail === "string") {
          return this.truncateUserMessage(errorData.detail);
        }
      }
      if (errorData.message) return this.truncateUserMessage(String(errorData.message));
      if (errorData.error) {
        if (typeof errorData.error === "string") return this.truncateUserMessage(errorData.error);
        if (errorData.error.message) return this.truncateUserMessage(String(errorData.error.message));
      }
      const errorString = JSON.stringify(errorData);
      if (errorString.length < 120) return errorString;
      return "Registration failed. Please check your information and try again.";
    },

    validateForm() {
      const fieldLabels = {
        pitch_tier: "Sport Facility Tier",
        pitch_name: "Sport Facility Name",
        pitch_type: "Sport Facility Type",
        pitch_description: "Sport Facility Description",
        hourly_rate: "Hourly Rate",
        address: "Address",
        county: "County",
        zone: "Zone",
        pin_location_url: "Pin Location URL",
      };
      const requiredFields = [
        "pitch_tier",
        "pitch_name",
        "pitch_type",
        "hourly_rate",
        "pitch_description",
        "address",
        "county",
        "zone",
        "pin_location_url",
      ];

      for (const field of requiredFields) {
        const raw = this.formData[field];
        if (raw === null || raw === undefined || String(raw).trim() === "") {
          const fieldName =
            fieldLabels[field] ||
            field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
          this.emitToast("Incomplete form", `Please fill in ${fieldName}.`, "warning");
          return false;
        }
      }

      const hourlyRate = Number(this.formData.hourly_rate);
      if (!Number.isFinite(hourlyRate) || hourlyRate <= 0) {
        this.emitToast(
          "Hourly rate",
          "Enter a valid hourly rate greater than 0 (KES).",
          "warning"
        );
        return false;
      }

      const pinUrl = String(this.formData.pin_location_url || "").trim();
      let pinUrlValid = false;
      try {
        const u = new URL(pinUrl.startsWith("http") ? pinUrl : `https://${pinUrl}`);
        pinUrlValid = Boolean(u.hostname);
      } catch {
        pinUrlValid = false;
      }
      if (!pinUrlValid) {
        this.emitToast("Invalid link", "Please enter a valid URL for pin location.", "warning");
        return false;
      }

      const desc = String(this.formData.pitch_description || "").trim();
      if (desc.length < 20) {
        this.emitToast(
          "Description too short",
          "Please provide a more detailed sport facility description (minimum 20 characters).",
          "warning"
        );
        return false;
      }

      if (!this.formData.agree_to_terms) {
        this.emitToast(
          "Terms required",
          "Please agree to the Facility Ownership Terms and Conditions.",
          "warning"
        );
        return false;
      }

      if (this.formData.pitch_images && this.formData.pitch_images.length > MAX_FACILITY_IMAGES) {
        this.emitToast(
          "Too many photos",
          `You can upload at most ${MAX_FACILITY_IMAGES} sport facility photos.`,
          "warning"
        );
        return false;
      }

      if (this.imageUploadError) {
        this.emitToast("Too many photos", this.imageUploadError, "warning");
        return false;
      }

      if (this.formData.pitch_images && this.formData.pitch_images.length > 0) {
        const maxSize = 10 * 1024 * 1024;
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        
        for (const file of this.formData.pitch_images) {
          if (file.size > maxSize) {
            this.emitToast(
              "File too large",
              `${file.name} exceeds the 10MB limit.`,
              "warning"
            );
            return false;
          }
          if (!allowedTypes.includes(file.type)) {
            this.emitToast(
              "Invalid file type",
              `${file.name} must be JPG or PNG.`,
              "warning"
            );
            return false;
          }
        }
      }

      return true;
    },
  }
};
</script>
<style scoped>
.image-upload-error {
  color: #e63946;
  font-size: 0.85rem;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

  .input-group textarea {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid var(--border-color-hover) !important;
  border-radius: 10px;
  font-size: 0.95rem;
  font-family: "Poppins", sans-serif;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--background-color2);
  color: var(--text-color1) !important;
  resize: vertical;
  min-height: 100px;
  max-height: 200px;
}

.input-group textarea:focus {
  outline: none;
  border-color: var(--border-color-hover) !important;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
  transform: translateY(-2px);
}

.input-group textarea::placeholder {
  color: var(--text-color2);
  opacity: 0.7;
}

.input-group textarea + small {
  display: flex;
  align-items: center;
  margin-top: 5px;
  color: var(--text-color2);
  font-size: 0.8rem;
}

.input-group textarea + small i {
  margin-right: 5px;
  color: var(--border-color-hover);
}
.acknowledgment-modal {
  position: fixed;
  top: 100px;
  right: 20px;
  z-index: 1000;
  animation: slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.acknowledgment-modal.info-modal {
  animation: slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.acknowledgment-modal .modal-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  min-width: 280px;
  max-width: 350px;
  transform-origin: center right;
  animation: modalPulse 2s infinite alternate;
}

.info-modal .modal-content {
  background: linear-gradient(135deg, #2196F3, #1976D2);
  color: white;
  border-left: 4px solid #0D47A1;
}

.acknowledgment-modal i {
  font-size: 1.5rem;
  flex-shrink: 0;
  animation: iconBounce 0.6s ease;
}

.acknowledgment-modal p {
  margin: 0;
  font-weight: 500;
  font-size: 0.95rem;
  line-height: 1.4;
  animation: textReveal 0.5s ease-out;
}

.already-registered-content {
  width: 100%;
}

.already-registered-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.dashboard-btn, .close-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 120px;
}

.dashboard-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.dashboard-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.close-btn {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.dashboard-btn i {
  margin-right: 5px;
  font-size: 0.9rem;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }
  to {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
}

@keyframes modalPulse {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(1.02);
  }
}

@keyframes iconBounce {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

@keyframes textReveal {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.input-group input,
.input-group select {
  border: 1px solid var(--border-color-hover) !important;
  color: var(--text-color1) !important;
}

.input-group input:focus,
.input-group select:focus,
.input-group input:active,
.input-group select:active {
  color: var(--text-color1) !important;
  border-color: var(--border-color-hover) !important;
  background-color: var(--background-color2) !important;
}

.input-group input::placeholder {
  color: var(--text-color2);
  opacity: 0.7;
}

.input-group small {
  display: block;
  margin-top: 5px;
  color: var(--text-color2);
  font-size: 0.8rem;
}

.input-group .input-readonly {
  cursor: default;
  background-color: rgba(0, 0, 0, 0.04) !important;
  opacity: 0.95;
}

.input-group .country-hint {
  margin-top: 6px;
}

.input-group input[type="file"] {
  padding: 10px;
  border: 2px dashed var(--border-color-hover);
  border-radius: 8px;
  background: var(--background-color1);
  width: 100%;
  transition: all 0.3s ease;
  color: var(--text-color1);
}

.input-group input[type="file"]:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-2px);
}

.submit-btn-inner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: inherit;
}

.submit-btn.loading {
  background: linear-gradient(135deg, var(--border-color-hover), #2c7744);
  position: relative;
  overflow: hidden;
}

.submit-btn.loading::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: buttonShimmer 1.5s infinite;
}

@keyframes buttonShimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@media (max-width: 768px) {
  .acknowledgment-modal {
    top: 80px;
    right: 10px;
    left: 10px;
    animation: slideInUp 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55), fadeOutUp 0.3s ease-in 2s forwards;
  }
  
  .acknowledgment-modal.info-modal {
    animation: slideInUp 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  }
  
  @keyframes slideInUp {
    from {
      transform: translateY(100%) scale(0.9);
      opacity: 0;
    }
    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }
  
  @keyframes fadeOutUp {
    from {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
    to {
      transform: translateY(-100%) scale(0.9);
      opacity: 0;
    }
  }
  
  .acknowledgment-modal .modal-content {
    min-width: auto;
    max-width: none;
    margin: 0 auto;
  }
  
  .input-group input[type="file"] {
    padding: 8px;
  }
  
  .input-group small {
    font-size: 0.75rem;
  }
  
  .already-registered-actions {
    flex-direction: column;
  }
  
  .dashboard-btn, .close-btn {
    min-width: 100%;
  }
}

.pitch-owner-registration {
  width: 100%;
  min-height: 100vh;
  background: var(--background-color1);
  font-family: "Poppins", sans-serif;
  overflow-x: hidden;
}

.navbar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);
  animation: slideDown 0.5s ease-out;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.hero-section {
  background: linear-gradient(135deg, var(--border-color-hover) 0%, #2c7744 100%);
  color: white;
  padding: 140px 20px 60px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%);
  animation: heroGlow 8s infinite alternate;
}

.hero-content h1 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 15px;
  animation: titleReveal 1s ease-out;
  text-shadow: 0 2px 10px rgba(0,0,0,0.2);
}

.hero-content p {
  font-size: 1.1rem;
  opacity: 0.9;
  max-width: 500px;
  margin: 0 auto;
  animation: subtitleReveal 1s ease-out 0.3s both;
}

@keyframes heroGlow {
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.8;
  }
}

@keyframes titleReveal {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes subtitleReveal {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Registration Container */
.registration-container {
  max-width: 800px;
  margin: -40px auto 60px;
  padding: 0 20px;
}

.form-card {
  background: var(--background-color2);
  border-radius: 20px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
  padding: 40px;
  opacity: 0;
  transform: translateY(30px) scale(0.98);
  transition: all 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  position: relative;
  overflow: hidden;
}

.form-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--border-color-hover), #2c7744);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.5s ease;
}

.form-card.visible::before {
  transform: scaleX(1);
}

.form-card.visible {
  opacity: 1;
  transform: translateY(0) scale(1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.form-header {
  text-align: center;
  margin-bottom: 40px;
}

.form-header h2 {
  font-size: 1.8rem;
  color: var(--text-color1);
  margin-bottom: 10px;
  position: relative;
  display: inline-block;
}

.form-header h2::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 3px;
  background: var(--border-color-hover);
  border-radius: 2px;
}

.form-header p {
  color: var(--text-color2);
  font-size: 0.95rem;
}

.contact-visibility-notice {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin: 0 0 28px;
  padding: 16px 18px;
  border-radius: 12px;
  border: 1px solid rgba(59, 130, 246, 0.35);
  background: rgba(59, 130, 246, 0.08);
  color: var(--text-color1);
  text-align: left;
}

.contact-visibility-notice > i {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 1.35rem;
  color: var(--border-color-hover, #3b82f6);
}

.contact-visibility-notice__body strong {
  display: block;
  font-size: 1rem;
  margin-bottom: 8px;
  color: var(--text-color1);
}

.contact-visibility-notice__body p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--text-color2);
}

.contact-visibility-notice__body p strong {
  display: inline;
  color: var(--text-color1);
  font-weight: 600;
}

/* Form Sections with enhanced animations */
.form-section {
  margin-bottom: 40px;
  padding-bottom: 30px;
  border-bottom: 1px solid var(--border-color);
  animation: sectionReveal 0.6s ease-out;
  animation-fill-mode: both;
}

.form-section:nth-child(1) { animation-delay: 0.1s; }
.form-section:nth-child(2) { animation-delay: 0.2s; }
.form-section:nth-child(3) { animation-delay: 0.3s; }
.form-section:nth-child(4) { animation-delay: 0.4s; }
.form-section:nth-child(5) { animation-delay: 0.5s; }

@keyframes sectionReveal {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.form-section:last-of-type {
  border-bottom: none;
}

.form-section h3 {
  font-size: 1.2rem;
  color: var(--text-color1);
  margin-bottom: 20px;
  font-weight: 600;
  position: relative;
  padding-left: 15px;
}

.form-section h3::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 5px;
  height: 20px;
  background: var(--border-color-hover);
  border-radius: 3px;
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.input-group {
  display: flex;
  flex-direction: column;
  position: relative;
}

.input-group.full-width {
  grid-column: 1 / -1;
}

.input-group label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-color1);
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.input-group input,
.input-group select {
  padding: 12px 15px;
  border: 1px solid var(--border-color-hover) !important;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--background-color2);
  animation: inputReveal 0.5s ease-out;
}

.input-group input:focus,
.input-group select:focus {
  outline: none;
  border-color: var(--border-color-hover) !important;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
  transform: translateY(-2px);
}

.input-group input:focus + label,
.input-group select:focus + label {
  color: var(--border-color-hover);
  transform: translateY(-2px);
}

@keyframes inputReveal {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.amenities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.amenity-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 2px solid var(--border-color);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--text-color1) !important;
  animation: checkboxReveal 0.4s ease-out;
  animation-fill-mode: both;
}

.amenity-checkbox:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.amenity-checkbox input[type="checkbox"] {
  display: none;
}

.amenity-checkbox .checkmark {
  width: 18px;
  height: 18px;
  border: 2px solid var(--border-color);
  border-radius: 4px;
  position: relative;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.amenity-checkbox input[type="checkbox"]:checked + .checkmark {
  background: var(--border-color-hover);
  border-color: var(--border-color-hover);
  animation: checkmarkPop 0.3s ease;
}

.amenity-checkbox input[type="checkbox"]:checked + .checkmark::after {
  content: "✓";
  color: var(--text-color3);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  font-weight: bold;
  animation: checkReveal 0.2s ease;
}

.amenity-checkbox i {
  color: var(--border-color-hover);
  width: 16px;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.amenity-checkbox:hover i {
  transform: scale(1.2);
}

@keyframes checkboxReveal {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes checkmarkPop {
  0% {
    transform: scale(0.8);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes checkReveal {
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}

.terms-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 25px;
  cursor: pointer;
  color: var(--text-color1);
  transition: all 0.3s ease;
  padding: 10px;
  border-radius: 8px;
}

.terms-checkbox-text {
  line-height: 1.5;
}

.terms-checkbox:hover {
  background: rgba(76, 175, 80, 0.05);
  transform: translateX(5px);
}

.terms-checkbox a {
  color: var(--border-color-hover);
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  position: relative;
}

.terms-checkbox a:hover {
  text-decoration: none;
}

.terms-checkbox a::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: var(--border-color-hover);
  transition: width 0.3s ease;
}

.terms-checkbox a:hover::after {
  width: 100%;
}

.form-actions {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.submit-btn,
.cancel-btn {
  padding: 14px 30px;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex: 1;
  min-width: 150px;
  position: relative;
  overflow: hidden;
}

.submit-btn::before,
.cancel-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.submit-btn:hover::before,
.cancel-btn:hover::before {
  width: 300px;
  height: 300px;
}

.submit-btn {
  background: var(--border-color-hover);
  color: var(--text-color3);
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.submit-btn:hover:not(:disabled) {
  background: var(--text-color2);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(76, 175, 80, 0.4);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.cancel-btn {
  background: transparent;
  color: var(--text-color2);
  border: 2px solid var(--border-color);
}

.cancel-btn:hover:not(:disabled) {
  border-color: var(--text-color2);
  transform: translateY(-3px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.benefits-section {
  max-width: 1200px;
  margin: 0 auto 80px;
  padding: 0 20px;
  text-align: center;
}

.benefits-section h2 {
  font-size: 2rem;
  color: var(--text-color1);
  margin-bottom: 50px;
  position: relative;
  display: inline-block;
}

.benefits-section h2::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, var(--border-color-hover), #2c7744);
  border-radius: 2px;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.benefit-card {
  padding: 30px 20px;
  background: var(--background-color2);
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: benefitReveal 0.6s ease-out;
  animation-fill-mode: both;
  position: relative;
  overflow: hidden;
}

.benefit-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--border-color-hover), #2c7744);
  transform: translateY(-100%);
  transition: transform 0.4s ease;
}

.benefit-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.benefit-card:hover::before {
  transform: translateY(0);
}

.benefit-card i {
  font-size: 2.5rem;
  color: var(--border-color-hover);
  margin-bottom: 20px;
  transition: all 0.4s ease;
}

.benefit-card:hover i {
  transform: scale(1.2) rotate(5deg);
}

.benefit-card h3 {
  font-size: 1.2rem;
  color: var(--text-color1);
  margin-bottom: 15px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.benefit-card:hover h3 {
  color: var(--border-color-hover);
}

.benefit-card p {
  color: var(--text-color2);
  font-size: 0.9rem;
  line-height: 1.5;
  transition: all 0.3s ease;
}

.benefit-card--coming-soon {
  padding-bottom: 48px;
}

.coming-soon-badge {
  display: inline-block;
  margin-top: 14px;
  padding: 4px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--border-color-hover);
  border: 1px solid rgba(44, 119, 68, 0.35);
  border-radius: 999px;
  cursor: help;
}

@keyframes benefitReveal {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 1200px) {
  .benefits-section {
    padding: 0 40px;
  }
}

@media (max-width: 992px) {
  .registration-container {
    max-width: 700px;
  }
  
  .benefits-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding: 120px 15px 40px;
  }
  
  .hero-content h1 {
    font-size: 2rem;
  }
  
  .hero-content p {
    font-size: 1rem;
  }
  
  .form-card {
    padding: 25px 20px;
    margin: -20px auto 40px;
    border-radius: 15px;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .amenities-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .form-actions {
    flex-direction: column;
    gap: 12px;
  }
  
  .submit-btn,
  .cancel-btn {
    min-width: 100%;
  }
  
  .benefits-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .benefits-section h2 {
    font-size: 1.6rem;
  }
  
  .form-section h3 {
    padding-left: 10px;
  }
  
  .form-section h3::before {
    height: 16px;
  }
  
}

@media (max-width: 576px) {
  .hero-section {
    padding: 100px 10px 30px;
  }
  
  .hero-content h1 {
    font-size: 1.7rem;
  }
  
  .registration-container {
    padding: 0 10px;
  }
  
  .form-card {
    padding: 20px 15px;
    margin: -15px auto 30px;
    border-radius: 12px;
  }
  
  .form-section {
    margin-bottom: 30px;
    padding-bottom: 20px;
  }
  
  .form-header h2 {
    font-size: 1.5rem;
  }
  
  .form-header p {
    font-size: 0.9rem;
  }
  
  .input-group input,
  .input-group select {
    padding: 10px 12px;
    font-size: 0.9rem;
  }
  
  .amenity-checkbox {
    padding: 10px;
    font-size: 0.9rem;
  }
  
  .terms-checkbox {
    font-size: 0.9rem;
    padding: 8px;
  }
  
  .submit-btn,
  .cancel-btn {
    padding: 12px 20px;
    font-size: 0.95rem;
  }
  
  .benefit-card {
    padding: 20px 15px;
  }
  
  .benefit-card i {
    font-size: 2rem;
    margin-bottom: 15px;
  }
  
  .benefit-card h3 {
    font-size: 1.1rem;
  }
  
  .benefit-card p {
    font-size: 0.85rem;
  }
  
}

@media (max-width: 400px) {
  .hero-content h1 {
    font-size: 1.5rem;
  }
  
  .form-header h2 {
    font-size: 1.3rem;
  }
  
  .benefits-section h2 {
    font-size: 1.4rem;
  }
  
  .input-group label {
    font-size: 0.85rem;
  }
  
  .amenity-checkbox {
    font-size: 0.85rem;
  }
  
}

@media (hover: none) and (pointer: coarse) {
  .submit-btn:hover,
  .cancel-btn:hover,
  .amenity-checkbox:hover,
  .benefit-card:hover {
    transform: none;
  }
  
  .submit-btn:active,
  .cancel-btn:active {
    transform: scale(0.98);
  }
  
  .amenity-checkbox:active {
    background: rgba(76, 175, 80, 0.05);
    transform: scale(0.98);
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  
  .fade-up {
    opacity: 1;
    transform: none;
  }
  
}

.fade-up {
  opacity: 0;
  transform: translateY(30px) scale(0.98);
  transition: all 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.fade-up.visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

@media (prefers-reduced-motion: no-preference) {
  .pitch-owner-registration {
    scroll-behavior: smooth;
  }
}
</style>